import React, { useMemo, useEffect, useRef, useState } from 'react';
import { FaUmbrellaBeach, FaCalendarDay, FaStar, FaUserTie } from 'react-icons/fa';

const HOUR_START = 6;
const HOUR_END = 23;
const TOTAL_HOURS = HOUR_END - HOUR_START;
const HOUR_HEIGHT = 80; // px per hour row (increased for better spacing)
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatHour = (h) => {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
};

const parseTime = (t) => {
  if (!t) return null;
  const [hh, mm] = t.split(':').map(Number);
  return { h: hh, m: mm };
};

const formatTimeShort = (t) => {
  if (!t) return '';
  const p = parseTime(t);
  if (!p) return '';
  const ampm = p.h >= 12 ? 'PM' : 'AM';
  const h12 = p.h === 0 ? 12 : p.h > 12 ? p.h - 12 : p.h;
  return p.m === 0 ? `${h12} ${ampm}` : `${h12}:${String(p.m).padStart(2, '0')} ${ampm}`;
};

const EvtIcon = ({ evt }) => {
  if (evt.type === 'HOLIDAY') return <FaUmbrellaBeach size={9} />;
  if (evt.isWorkingDay === true) return <FaStar size={9} />;
  return <FaCalendarDay size={9} />;
};

const WeekGrid = ({ weekStart, events, batches = [], interviewEvents = [], workingDays, onDateClick, onEventClick, isDayView = false, showNonWorkingDays = true }) => {
  const scrollRef = useRef(null);
  const [, setTick] = useState(0);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);

  /* ── Days to display (1 for day view, 7 or filtered for week view) ── */
  const weekDays = useMemo(() => {
    if (isDayView) {
      const d = new Date(weekStart);
      d.setHours(0, 0, 0, 0);
      return [d];
    }
    const all7 = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      d.setHours(0, 0, 0, 0);
      return d;
    });
    if (!showNonWorkingDays) {
      return all7.filter((d) => workingDays.includes(d.getDay()));
    }
    return all7;
  }, [weekStart, isDayView, showNonWorkingDays, workingDays]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayColIndex = useMemo(() => {
    return weekDays.findIndex((d) => d.toDateString() === today.toDateString());
  }, [weekDays, today]);

  const hours = useMemo(
    () => Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i),
    []
  );

  /* ── Separate all-day vs timed events ── */
  const { allDayEvents, timedEvents } = useMemo(() => {
    const ad = [];
    const td = [];
    for (const evt of events) {
      if (evt.allDay !== false) ad.push(evt);
      else td.push(evt);
    }
    return { allDayEvents: ad, timedEvents: td };
  }, [events]);

  /* ── Index events by date string ── */
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const evt of events) {
      const s = new Date(evt.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(evt.endDate); e.setHours(0, 0, 0, 0);
      const c = new Date(s);
      while (c <= e) {
        const k = c.toDateString();
        if (!map[k]) map[k] = [];
        if (!map[k].find((x) => x._id === evt._id)) map[k].push(evt);
        c.setDate(c.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  /* ── Index batches by date string ── */
  const batchesByDate = useMemo(() => {
    const map = {};
    for (const batch of batches) {
      const batchWorkingDays = batch.batchType === "WEEKEND" ? [0, 6] : workingDays;
      if (batch.startDate) {
        const start = new Date(batch.startDate);
        start.setHours(0, 0, 0, 0);
        const maxDate = new Date(start);
        maxDate.setDate(maxDate.getDate() + 60);
        const c = new Date(start);
        while (c <= maxDate) {
          if (batchWorkingDays.includes(c.getDay())) {
            const k = c.toDateString();
            if (!map[k]) map[k] = [];
            if (!map[k].find((x) => x._id === batch._id)) map[k].push(batch);
          }
          c.setDate(c.getDate() + 1);
        }
      }
    }
    return map;
  }, [batches, workingDays]);

  /* ── Layout all-day spanning events ── */
  const allDayLayout = useMemo(() => {
    const segments = [];
    const seen = new Set();
    for (const evt of allDayEvents) {
      if (seen.has(evt._id)) continue;
      seen.add(evt._id);
      const evtStart = new Date(evt.startDate); evtStart.setHours(0, 0, 0, 0);
      const evtEnd = new Date(evt.endDate); evtEnd.setHours(0, 0, 0, 0);
      let startCol = -1;
      let endCol = -1;
      for (let i = 0; i < weekDays.length; i++) {
        if (weekDays[i] >= evtStart && weekDays[i] <= evtEnd) {
          if (startCol === -1) startCol = i;
          endCol = i;
        }
      }
      if (startCol !== -1) segments.push({ event: evt, startCol, endCol });
    }
    segments.sort(
      (a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol)
    );
    const rows = [];
    for (const seg of segments) {
      let placed = false;
      for (let r = 0; r < rows.length; r++) {
        const conflict = rows[r].some(
          ([s, e]) => !(seg.endCol < s || seg.startCol > e)
        );
        if (!conflict) {
          rows[r].push([seg.startCol, seg.endCol]);
          seg.row = r;
          placed = true;
          break;
        }
      }
      if (!placed) {
        rows.push([[seg.startCol, seg.endCol]]);
        seg.row = rows.length - 1;
      }
    }
    return { segments, rowCount: rows.length };
  }, [allDayEvents, weekDays]);

  /* ── Compute timed event positions per day column ── */
  /* ── Unified layout: merge events + batches + interviews, then detect overlaps together ── */
  const { timedEventLayout, batchLayout, interviewLayout } = useMemo(() => {
    // 1. Build a unified list of ALL timed items (events + batch sessions + interviews)
    const allItems = [];

    // Add timed events
    for (const evt of timedEvents) {
      const evtDate = new Date(evt.startDate); evtDate.setHours(0, 0, 0, 0);
      const col = weekDays.findIndex((d) => d.toDateString() === evtDate.toDateString());
      if (col === -1) continue;
      const st = parseTime(evt.startTime);
      const et = parseTime(evt.endTime);
      if (!st || !et) continue;
      const startMin = (st.h - HOUR_START) * 60 + st.m;
      const endMin = (et.h - HOUR_START) * 60 + et.m;
      const topPx = (startMin / 60) * HOUR_HEIGHT;
      const heightPx = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 20);
      allItems.push({ kind: 'event', event: evt, col, topPx, heightPx, startMin, endMin });
    }

    // Add batch sessions
    for (const batch of batches) {
      if (!batch.startTime || !batch.endTime) continue;
      const batchWorkingDays = batch.batchType === "WEEKEND" ? [0, 6] : workingDays;
      for (let i = 0; i < weekDays.length; i++) {
        const date = weekDays[i];
        if (!batchWorkingDays.includes(date.getDay())) continue;
        const batchStart = new Date(batch.startDate);
        batchStart.setHours(0, 0, 0, 0);
        if (date < batchStart) continue;
        const st = parseTime(batch.startTime);
        const et = parseTime(batch.endTime);
        if (!st || !et) continue;
        const startMin = (st.h - HOUR_START) * 60 + st.m;
        const endMin = (et.h - HOUR_START) * 60 + et.m;
        const topPx = (startMin / 60) * HOUR_HEIGHT;
        const heightPx = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 20);
        allItems.push({ kind: 'batch', batch, col: i, topPx, heightPx, startMin, endMin });
      }
    }

    // Add interview events
    for (const intv of interviewEvents) {
      if (!intv.date || !intv.startTime) continue;
      const evtDate = new Date(intv.date); evtDate.setHours(0, 0, 0, 0);
      const col = weekDays.findIndex((d) => d.toDateString() === evtDate.toDateString());
      if (col === -1) continue;
      const st = parseTime(intv.startTime);
      const et = intv.endTime ? parseTime(intv.endTime) : null;
      if (!st) continue;
      const startMin = (st.h - HOUR_START) * 60 + st.m;
      const endMin = et ? (et.h - HOUR_START) * 60 + et.m : startMin + (intv.duration || 60);
      const topPx = (startMin / 60) * HOUR_HEIGHT;
      const heightPx = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 20);
      allItems.push({ kind: 'interview', interview: intv, col, topPx, heightPx, startMin, endMin });
    }

    // 2. Group by column
    const byCol = {};
    for (const item of allItems) {
      if (!byCol[item.col]) byCol[item.col] = [];
      byCol[item.col].push(item);
    }

    // 3. Overlap detection — cluster-based for consistent widths
    for (const col of Object.keys(byCol)) {
      const items = byCol[col].sort((a, b) => a.startMin - b.startMin);
      if (items.length === 0) continue;

      // Step A: Build connected overlap clusters using union-find
      // Two items overlap if: a.startMin < b.endMin && b.startMin < a.endMin
      const parent = items.map((_, i) => i);
      const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
      const union = (a, b) => { parent[find(a)] = find(b); };

      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          if (items[j].startMin < items[i].endMin) {
            union(i, j);
          } else {
            break; // sorted by startMin, no more overlaps
          }
        }
      }

      // Step B: Group items by cluster
      const clusters = {};
      for (let i = 0; i < items.length; i++) {
        const root = find(i);
        if (!clusters[root]) clusters[root] = [];
        clusters[root].push(i);
      }

      // Step C: For each cluster, assign lanes and compute max concurrent
      for (const indices of Object.values(clusters)) {
        const clusterItems = indices.map((i) => items[i]);
        clusterItems.sort((a, b) => a.startMin - b.startMin);

        // Greedy lane assignment within this cluster
        const lanes = [];
        for (const item of clusterItems) {
          let placed = false;
          for (let l = 0; l < lanes.length; l++) {
            if (lanes[l] <= item.startMin) {
              lanes[l] = item.endMin;
              item.lane = l;
              placed = true;
              break;
            }
          }
          if (!placed) {
            item.lane = lanes.length;
            lanes.push(item.endMin);
          }
        }

        // All items in this cluster share the same totalLanes
        const clusterLanes = lanes.length;
        for (const item of clusterItems) {
          item.totalLanes = clusterLanes;
          item.isFullWidth = clusterLanes === 1;
        }
      }
    }

    // 4. Split back into events vs batches vs interviews
    const evtLayout = {};
    const batchLay = {};
    const intLayout = {};
    for (const col of Object.keys(byCol)) {
      for (const item of byCol[col]) {
        if (item.kind === 'event') {
          if (!evtLayout[col]) evtLayout[col] = [];
          evtLayout[col].push(item);
        } else if (item.kind === 'interview') {
          if (!intLayout[col]) intLayout[col] = [];
          intLayout[col].push(item);
        } else {
          if (!batchLay[col]) batchLay[col] = [];
          batchLay[col].push(item);
        }
      }
    }
    return { timedEventLayout: evtLayout, batchLayout: batchLay, interviewLayout: intLayout };
  }, [timedEvents, batches, interviewEvents, weekDays, workingDays]);

  /* ── Non-working day check ── */
  const isNonWorking = (date) => {
    const dayEvts = eventsByDate[date.toDateString()] || [];
    for (const e of dayEvts) {
      if (e.isWorkingDay === false) return true;
      if (e.isWorkingDay === true) return false;
    }
    return !workingDays.includes(date.getDay());
  };

  /* ── Now indicator ── */
  const now = new Date();
  const nowH = now.getHours();
  const nowM = now.getMinutes();
  const showNow = todayColIndex >= 0 && nowH >= HOUR_START && nowH < HOUR_END;
  const nowTopPx = showNow
    ? ((nowH - HOUR_START) * 60 + nowM) / 60 * HOUR_HEIGHT
    : 0;

  // Auto-scroll to current time
  useEffect(() => {
    if (scrollRef.current && showNow) {
      scrollRef.current.scrollTop = Math.max(0, nowTopPx - scrollRef.current.clientHeight / 3);
    }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh now-line every minute
  useEffect(() => {
    const id = setInterval(() => setTick((c) => c + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const evtClass = (evt) => {
    if (evt.type === 'HOLIDAY') return 'wk-evt-holiday';
    if (evt.isWorkingDay === true) return 'wk-evt-override';
    return 'wk-evt-event';
  };

  const numCols = weekDays.length; // 1 for day view, 7 for week view

  return (
    <div className={`wk-container${isDayView ? ' wk-day-mode' : ''}`}>
      {/* ── Day column headers ── */}
      <div className="wk-day-headers" style={{ gridTemplateColumns: `60px repeat(${numCols}, 1fr)` }}>
        <div className="wk-gutter" />
        {weekDays.map((day, i) => {
          const isTdy = i === todayColIndex;
          const off = isNonWorking(day);
          return (
            <div
              key={i}
              className={`wk-day-col-header${isTdy ? ' wk-day-today' : ''}${off ? ' wk-day-off' : ''}`}
              onClick={() => onDateClick?.(day)}
            >
              <span className={`wk-day-num${isTdy ? ' wk-day-num-today' : ''}`}>
                {String(day.getDate()).padStart(2, '0')}
              </span>
              <span className="wk-day-name">{isDayView ? DAY_NAMES_FULL[day.getDay()] : DAY_NAMES[day.getDay()]}</span>
            </div>
          );
        })}
      </div>

      {/* ── All-day event bar ── */}
      {allDayLayout.rowCount > 0 && (
        <div className="wk-allday-section">
          <div className="wk-gutter wk-allday-gutter">
            <span className="wk-allday-label">All day</span>
          </div>
          <div
            className="wk-allday-grid"
            style={{ gridTemplateColumns: `repeat(${numCols}, 1fr)`, gridTemplateRows: `repeat(${allDayLayout.rowCount}, 26px)` }}
          >
            {allDayLayout.segments.map((seg) => (
              <div
                key={seg.event._id}
                className={`wk-allday-event ${evtClass(seg.event)}`}
                style={{
                  gridColumn: `${seg.startCol + 1} / ${seg.endCol + 2}`,
                  gridRow: seg.row + 1,
                }}
                onClick={(e) => { e.stopPropagation(); onEventClick?.(seg.event); }}
                title={seg.event.title}
              >
                <EvtIcon evt={seg.event} />
                <span className="wk-allday-text">{seg.event.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Scrollable time grid ── */}
      <div className="wk-scroll" ref={scrollRef}>
        <div className="wk-time-body" style={{ height: TOTAL_HOURS * HOUR_HEIGHT }}>
          {/* Hour rows (background lines) */}
          {hours.map((hour) => (
            <div
              className="wk-time-row"
              key={hour}
              style={{ height: HOUR_HEIGHT, gridTemplateColumns: `60px repeat(${numCols}, 1fr)` }}
            >
              <div className="wk-gutter wk-time-gutter">
                <span className="wk-time-label">{formatHour(hour)}</span>
              </div>
              {weekDays.map((day, i) => {
                const off = isNonWorking(day);
                const isTdy = i === todayColIndex;
                return (
                  <div
                    key={i}
                    className={`wk-time-cell${off ? ' wk-time-cell-off' : ''}${isTdy ? ' wk-time-cell-today' : ''}`}
                    onClick={() => onDateClick?.(day)}
                  />
                );
              })}
            </div>
          ))}

          {/* ── Timed event blocks (absolute positioned) ── */}
          {Object.values(timedEventLayout).flat().map((item) => {
            return (
              <div
                key={item.event._id}
                className={`wk-timed-event ${evtClass(item.event)}`}
                style={{
                  top: item.topPx,
                  height: item.heightPx,
                  left: item.isFullWidth 
                    ? `calc(60px + (100% - 60px) * ${item.col} / ${numCols} + 3px)`
                    : `calc(60px + (100% - 60px) * ${item.col} / ${numCols} + (100% - 60px) / ${numCols} * ${item.lane / item.totalLanes})`,
                  width: item.isFullWidth
                    ? `calc((100% - 60px) / ${numCols} - 6px)`
                    : `calc((100% - 60px) / ${numCols} * ${1 / item.totalLanes} - 2px)`,
                }}
                onClick={(e) => { e.stopPropagation(); onEventClick?.(item.event); }}
                title={`${item.event.title} · ${formatTimeShort(item.event.startTime)} – ${formatTimeShort(item.event.endTime)}`}
              >
                <div className="wk-timed-title">{item.event.title}</div>
                <div className="wk-timed-time">
                  {formatTimeShort(item.event.startTime)} – {formatTimeShort(item.event.endTime)}
                </div>
              </div>
            );
          })}

          {/* ── Batch session blocks (absolute positioned) ── */}
          {Object.values(batchLayout).flat().map((item) => {
            return (
              <div
                key={`${item.batch._id}-${item.col}`}
                className="wk-batch-session"
                style={{
                  top: item.topPx,
                  height: item.heightPx,
                  left: item.isFullWidth 
                    ? `calc(60px + (100% - 60px) * ${item.col} / ${numCols} + 3px)`
                    : `calc(60px + (100% - 60px) * ${item.col} / ${numCols} + (100% - 60px) / ${numCols} * ${item.lane / item.totalLanes})`,
                  width: item.isFullWidth
                    ? `calc((100% - 60px) / ${numCols} - 6px)`
                    : `calc((100% - 60px) / ${numCols} * ${1 / item.totalLanes} - 2px)`,
                }}
                onClick={(e) => { e.stopPropagation(); setSelectedBatch(item.batch); }}
                title={`${item.batch.name} (${item.batch.batchType}) · ${formatTimeShort(item.batch.startTime)} – ${formatTimeShort(item.batch.endTime)}`}
              >
                <div className="wk-batch-title">{item.batch.name}</div>
                <div className="wk-batch-time">
                  {formatTimeShort(item.batch.startTime)} – {formatTimeShort(item.batch.endTime)}
                </div>
                {item.batch.course && (
                  <div className="wk-batch-meta">{item.batch.course.name}</div>
                )}
                {item.batch.instructor && (
                  <div className="wk-batch-meta">{item.batch.instructor.name}</div>
                )}
              </div>
            );
          })}

          {/* ── Interview event blocks (absolute positioned) ── */}
          {Object.values(interviewLayout).flat().map((item) => {
            return (
              <div
                key={item.interview._id}
                className="wk-interview-session"
                style={{
                  top: item.topPx,
                  height: item.heightPx,
                  left: item.isFullWidth 
                    ? `calc(60px + (100% - 60px) * ${item.col} / ${numCols} + 3px)`
                    : `calc(60px + (100% - 60px) * ${item.col} / ${numCols} + (100% - 60px) / ${numCols} * ${item.lane / item.totalLanes})`,
                  width: item.isFullWidth
                    ? `calc((100% - 60px) / ${numCols} - 6px)`
                    : `calc((100% - 60px) / ${numCols} * ${1 / item.totalLanes} - 2px)`,
                }}
                onClick={(e) => { e.stopPropagation(); setSelectedInterview(item.interview); }}
                title={`${item.interview.title || item.interview.companyName || 'Interview'} · ${formatTimeShort(item.interview.startTime)}${item.interview.endTime ? ` – ${formatTimeShort(item.interview.endTime)}` : ''}`}
              >
                <div className="wk-interview-title">
                  <FaUserTie size={9} style={{ marginRight: 3, flexShrink: 0 }} />
                  {item.interview.title || item.interview.companyName || 'Interview'}
                </div>
                <div className="wk-interview-time">
                  {formatTimeShort(item.interview.startTime)}{item.interview.endTime ? ` – ${formatTimeShort(item.interview.endTime)}` : ''}
                </div>
                {item.interview.roundType && (
                  <div className="wk-interview-meta">{item.interview.roundType}</div>
                )}
              </div>
            );
          })}

          {/* ── Now indicator ── */}
          {showNow && (
            <div
              className="wk-now-line"
              style={{ top: nowTopPx }}
            >
              <div
                className="wk-now-dot"
                style={{
                  left: `calc((100% - 60px) * ${todayColIndex} / ${numCols} - 5px)`,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Batch Details Modal ── */}
      {selectedBatch && (
        <div className="wk-batch-modal-overlay" onClick={() => setSelectedBatch(null)}>
          <div className="wk-batch-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wk-batch-modal-header">
              <h3>{selectedBatch.name}</h3>
              <button className="wk-batch-modal-close" onClick={() => setSelectedBatch(null)}>×</button>
            </div>
            <div className="wk-batch-modal-body">
              <div className="wk-batch-modal-row">
                <span className="wk-batch-modal-label">Batch Type:</span>
                <span className={`wk-batch-modal-badge wk-batch-modal-badge-${selectedBatch.batchType?.toLowerCase()}`}>
                  {selectedBatch.batchType || 'WEEKDAY'}
                </span>
              </div>
              <div className="wk-batch-modal-row">
                <span className="wk-batch-modal-label">Time:</span>
                <span className="wk-batch-modal-value">
                  {formatTimeShort(selectedBatch.startTime)} – {formatTimeShort(selectedBatch.endTime)}
                </span>
              </div>
              {selectedBatch.course && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Course:</span>
                  <span className="wk-batch-modal-value">{selectedBatch.course.name}</span>
                </div>
              )}
              {selectedBatch.instructor && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Instructor:</span>
                  <span className="wk-batch-modal-value">{selectedBatch.instructor.name}</span>
                </div>
              )}
              {selectedBatch.startDate && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Start Date:</span>
                  <span className="wk-batch-modal-value">
                    {new Date(selectedBatch.startDate).toLocaleDateString('en-US', { 
                      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata'
                    })}
                  </span>
                </div>
              )}
              {selectedBatch.status && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Status:</span>
                  <span className="wk-batch-modal-value">{selectedBatch.status}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Interview Details Modal ── */}
      {selectedInterview && (
        <div className="wk-batch-modal-overlay" onClick={() => setSelectedInterview(null)}>
          <div className="wk-batch-modal wk-interview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wk-batch-modal-header wk-interview-modal-header">
              <h3><FaUserTie size={14} style={{ marginRight: 6 }} />{selectedInterview.title || selectedInterview.companyName || 'Interview'}</h3>
              <button className="wk-batch-modal-close" onClick={() => setSelectedInterview(null)}>×</button>
            </div>
            <div className="wk-batch-modal-body">
              {selectedInterview.roundType && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Round:</span>
                  <span className="wk-batch-modal-value">{selectedInterview.roundType}</span>
                </div>
              )}
              {selectedInterview.companyName && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Company:</span>
                  <span className="wk-batch-modal-value">{selectedInterview.companyName}</span>
                </div>
              )}
              <div className="wk-batch-modal-row">
                <span className="wk-batch-modal-label">Date:</span>
                <span className="wk-batch-modal-value">
                  {new Date(selectedInterview.date).toLocaleDateString('en-US', { 
                    year: 'numeric', month: 'short', day: 'numeric', weekday: 'short', timeZone: 'Asia/Kolkata'
                  })}
                </span>
              </div>
              <div className="wk-batch-modal-row">
                <span className="wk-batch-modal-label">Time:</span>
                <span className="wk-batch-modal-value">
                  {formatTimeShort(selectedInterview.startTime)}{selectedInterview.endTime ? ` – ${formatTimeShort(selectedInterview.endTime)}` : ''}
                  {selectedInterview.duration ? ` (${selectedInterview.duration} min)` : ''}
                </span>
              </div>
              {selectedInterview.mode && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Mode:</span>
                  <span className="wk-batch-modal-value">{selectedInterview.mode}</span>
                </div>
              )}
              {selectedInterview.meetingLink && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Meeting:</span>
                  <span className="wk-batch-modal-value">
                    <a href={selectedInterview.meetingLink} target="_blank" rel="noopener noreferrer">Join Meeting</a>
                  </span>
                </div>
              )}
              {selectedInterview.location && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Location:</span>
                  <span className="wk-batch-modal-value">{selectedInterview.location}</span>
                </div>
              )}
              {selectedInterview.status && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Status:</span>
                  <span className={`wk-batch-modal-badge wk-batch-modal-badge-${selectedInterview.status?.toLowerCase()}`}>
                    {selectedInterview.status}
                  </span>
                </div>
              )}
              {selectedInterview.studentId && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Student:</span>
                  <span className="wk-batch-modal-value">{selectedInterview.studentId?.name || selectedInterview.studentId}</span>
                </div>
              )}
              {selectedInterview.interviewers?.length > 0 && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Interviewers:</span>
                  <span className="wk-batch-modal-value">
                    {selectedInterview.interviewers.map(i => i.name || i).join(', ')}
                  </span>
                </div>
              )}
              {selectedInterview.notes && (
                <div className="wk-batch-modal-row">
                  <span className="wk-batch-modal-label">Notes:</span>
                  <span className="wk-batch-modal-value">{selectedInterview.notes}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekGrid;
